/**
 * Right-click context menu for waypoint markers. Renders as a DOM popover
 * anchored at the screen-pixel position captured when the user right-clicked
 * a waypoint circle on the map; reads its open/close state from the store
 * (`contextMenuTarget`).
 *
 * Items:
 *   - Delete
 *   - Insert before / Insert after  (new waypoint at the same lng/lat)
 *   - Duplicate
 *   - Copy leg params               (snapshots THIS waypoint's legParams)
 *   - Paste leg params              (writes clipboardLegParams onto THIS one;
 *                                    disabled when the clipboard is empty)
 *
 * Closed on:
 *   - Click on any item (after the item's action runs)
 *   - Click outside (any mousedown anywhere else in the document)
 *   - Escape key
 *
 * Mounted once at EditorPage level so it survives panel toggles.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { useUIStore } from '../../stores/uiStore';
import type { DemoLegParams } from './types';
import './WaypointContextMenu.css';

export default function WaypointContextMenu() {
  const target = useDockDemoStore((s) => s.contextMenuTarget);
  const setTarget = useDockDemoStore((s) => s.setContextMenuTarget);
  const missions = useDockDemoStore((s) => s.missions);
  const clipboardLegParams = useDockDemoStore((s) => s.clipboardLegParams);
  const setClipboardLegParams = useDockDemoStore((s) => s.setClipboardLegParams);
  const deleteWaypoint = useDockDemoStore((s) => s.deleteWaypoint);
  const insertWaypointAt = useDockDemoStore((s) => s.insertWaypointAt);
  const duplicateWaypoint = useDockDemoStore((s) => s.duplicateWaypoint);
  const updateWaypointLegParams = useDockDemoStore((s) => s.updateWaypointLegParams);
  const showConfirm = useUIStore((s) => s.showConfirm);

  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on outside mousedown / Escape. We watch mousedown (not click) so
  // dragging-to-select inside the menu doesn't bubble into a close before
  // the item's click event fires.
  useEffect(() => {
    if (!target) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target as Node)) return;
      setTarget(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setTarget(null);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [target, setTarget]);

  if (!target) return null;

  const mission = missions.find((m) => m.id === target.missionId);
  const wp = mission?.waypoints.find((w) => w.id === target.waypointId);
  if (!mission || !wp) {
    // The waypoint was removed (e.g., via Clear auto-routed) while the menu
    // was open — fail silently by closing.
    return null;
  }
  const waypointIndex = mission.waypoints.findIndex((w) => w.id === wp.id);

  const close = () => setTarget(null);

  const onDelete = () => {
    // Close the menu first, then confirm — the confirm dialog owns the screen.
    const missionId = mission.id;
    const waypointId = wp.id;
    const label = `Waypoint #${waypointIndex + 1}`;
    close();
    showConfirm({
      title: 'Delete waypoint',
      message: `Delete ${label}? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      onConfirm: () => deleteWaypoint(missionId, waypointId),
    });
  };
  const onInsertBefore = () => {
    insertWaypointAt(mission.id, waypointIndex, wp.lng, wp.lat);
    close();
  };
  const onInsertAfter = () => {
    insertWaypointAt(mission.id, waypointIndex + 1, wp.lng, wp.lat);
    close();
  };
  const onDuplicate = () => {
    duplicateWaypoint(mission.id, wp.id);
    close();
  };
  const onCopyLegParams = () => {
    // Snapshot the current legParams (may be undefined — captured as null,
    // which serializes as "no params to paste" downstream).
    const lp: DemoLegParams | null = wp.legParams ? { ...wp.legParams } : null;
    setClipboardLegParams(lp);
    close();
  };
  const onPasteLegParams = () => {
    if (!clipboardLegParams) return;
    // Replace legParams wholesale — the existing action shallow-merges, so
    // we pre-clear any leftover fields by undefining them first.
    updateWaypointLegParams(mission.id, wp.id, {
      gimbalPitch: clipboardLegParams.gimbalPitch,
      gimbalYaw: clipboardLegParams.gimbalYaw,
      recordVideo: clipboardLegParams.recordVideo,
      recordVideoLens: clipboardLegParams.recordVideoLens,
      intervalPhoto: clipboardLegParams.intervalPhoto,
    });
    close();
  };

  // Clamp the menu to the viewport so a right-click near the right/bottom
  // edge doesn't cut off items. Width / height estimates are conservative;
  // the menu adapts via min-width in CSS.
  const menuW = 200;
  const menuH = 280;
  const left = Math.min(target.x, window.innerWidth - menuW - 4);
  const top = Math.min(target.y, window.innerHeight - menuH - 4);

  return createPortal(
    <div
      ref={menuRef}
      className="dock-demo-waypoint-menu"
      style={{ left, top }}
      role="menu"
    >
      <div className="dock-demo-waypoint-menu__header">
        Waypoint #{waypointIndex + 1}
      </div>
      <button type="button" role="menuitem" onClick={onInsertBefore}>
        Insert before
      </button>
      <button type="button" role="menuitem" onClick={onInsertAfter}>
        Insert after
      </button>
      <button type="button" role="menuitem" onClick={onDuplicate}>
        Duplicate
      </button>
      <div className="dock-demo-waypoint-menu__sep" />
      <button type="button" role="menuitem" onClick={onCopyLegParams}>
        Copy leg params
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={onPasteLegParams}
        disabled={!clipboardLegParams}
        title={clipboardLegParams ? '' : 'Copy leg params from a waypoint first'}
      >
        Paste leg params
      </button>
      <div className="dock-demo-waypoint-menu__sep" />
      <button
        type="button"
        role="menuitem"
        className="dock-demo-waypoint-menu__danger"
        onClick={onDelete}
      >
        Delete
      </button>
    </div>,
    document.body,
  );
}
